// Tres voces, tres trabajos: Instrument Serif dice (titulares y cifras
// grandes), Archivo explica (interfaz y texto corrido), IBM Plex Mono cuenta
// (fechas, conteos, etiquetas). Autoalojadas, para que el tablero se vea igual
// dentro de la red de LALA y sin el parpadeo de cargarlas desde un tercero.
import '@fontsource/instrument-serif/400.css'
import '@fontsource/instrument-serif/400-italic.css'
import '@fontsource/archivo/400.css'
import '@fontsource/archivo/500.css'
import '@fontsource/archivo/600.css'
import '@fontsource/archivo/700.css'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import '@fontsource/ibm-plex-mono/600.css'

import { ClerkProvider, SignedIn, SignedOut, SignInButton, UserButton, useAuth } from '@clerk/clerk-react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CssBaseline from '@mui/material/CssBaseline'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ThemeProvider } from '@mui/material/styles'
import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'

import { App } from './App'
import { APP, AUTH } from './labels'
import { setTokenGetter } from './lib/api'
import { CANVAS, FONT_MONO, INK, INK_FAINT, INK_SOFT, theme } from './theme'

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined

/**
 * Puente entre Clerk y el cliente HTTP.
 *
 * `getToken` se registra una sola vez y el cliente lo llama en cada request,
 * de modo que un token renovado se usa sin recargar la página. La app no se
 * monta hasta tenerlo: si lo hiciera, la primera tanda de llamadas saldría sin
 * Authorization y rebotaría con 401.
 */
function AuthenticatedApp() {
  const { getToken, isLoaded } = useAuth()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setTokenGetter(async () => getToken())
    setReady(true)
  }, [getToken])

  if (!isLoaded || !ready) return null
  return <App userSlot={<UserButton afterSignOutUrl="/" />} />
}

function SignInScreen() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: '100vh', bgcolor: CANVAS, px: 3 }}
    >
      <Paper sx={{ p: 5, maxWidth: 420, width: '100%' }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '12px',
            bgcolor: INK,
            color: CANVAS,
            display: 'grid',
            placeItems: 'center',
            fontFamily: FONT_MONO,
            fontSize: 17,
            mb: 3,
          }}
        >
          ◗
        </Box>

        <Typography
          sx={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.02 }}
        >
          {APP.title}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'block',
            fontFamily: FONT_MONO,
            fontSize: '0.625rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: INK_FAINT,
            mt: 1,
          }}
        >
          {APP.subtitle}
        </Box>

        <Typography variant="body2" sx={{ my: 4, color: INK_SOFT }}>
          {AUTH.signInHint}
        </Typography>

        <SignInButton mode="modal">
          <Button variant="contained" fullWidth sx={{ height: 44 }}>
            {AUTH.signIn}
          </Button>
        </SignInButton>
      </Paper>
    </Stack>
  )
}

/**
 * Sin llave de Clerk la app corre en modo local contra un backend en `dev`.
 * En producción la llave siempre está presente (la inyecta el build), y el
 * backend además se niega a responder sin Clerk configurado — así que este
 * camino no puede convertirse en una puerta trasera.
 */
function Root() {
  if (!PUBLISHABLE_KEY) {
    return <App />
  }
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <SignedIn>
        <AuthenticatedApp />
      </SignedIn>
      <SignedOut>
        <SignInScreen />
      </SignedOut>
    </ClerkProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh' }}>
        <Root />
      </Box>
    </ThemeProvider>
  </StrictMode>,
)
